import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaAction } from 'src/app/model/mtma-action';
import { CustomValidators } from 'src/app/validators/custom.validators';
import { MtmaKeypad } from 'src/app/model/mtma-keypad';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { MtmaTag } from 'src/app/model/mtma-tag';
import { MtmaModule } from 'src/app/model/mtma-module';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { MtmaRule } from 'src/app/model/mtma-rules-access';

@Component({
	selector: 'app-mtma-manage-keypad-dialog',
	templateUrl: './mtma-manage-keypad-dialog.component.html',
	providers: [{
		provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
	}]
})

export class MtmaKeypadDialogComponent implements OnInit, OnDestroy {
	keypadForm: FormGroup;
	module: MtmaModule;

	keypadsLimit: boolean;
	keypadsLimitLen: number;

	mtmaKeypadModifySub: Subscription;
	keypad: MtmaKeypad;

	adding = true;

	mtmaActionsSub: Subscription;

	mtmaActions: MtmaAction[] = [];

	listRules: MtmaRule[];
	listRulesSub: Subscription;

	dateTransform: DatePipe;
	mtmaModuleSub: Subscription;

	// design
	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private translate: TranslateService,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
	) {
		if (config.data !== undefined && config.data.keypad !== undefined) {
			this.keypad = config.data.keypad;
			this.adding = false;
		} else {
			this.keypad = new MtmaKeypad();
		}
	}

	ngOnInit(): void {
		this.mtmaModuleSub = this.store.select('device', 'mtmaModules').subscribe(list => {
			const module = list.filter(item => item.Type === 4)[0];
			this.module = module;
			this.keypadsLimit = false;
			this.keypadsLimitLen = 0;
			if (module !== undefined && module.Config !== null && module.Config !== undefined && module.Config.UseMode === 1) {
				this.keypadsLimit = true;
				this.keypadsLimitLen = module.Config.CodeLen;
			}
		});
		const curreLang = this.translate.currentLang;
		this.dateTransform = new DatePipe(curreLang);
		this.keypadForm = new FormGroup({
			'Description': new FormControl(this.keypad.Description, [Validators.maxLength(48)]),
			'Code': new FormControl(this.keypad.Code, [Validators.required, Validators.minLength(4),
			Validators.maxLength(8), Validators.pattern('[0-9]+')]),
			'Action1': new FormControl(this.keypad.Actions[0], Validators.required),
			'Action2': new FormControl(this.keypad.Actions[1], Validators.required),
			'Rule': new FormControl(this.keypad.RuleId),
			'StartDate': new FormControl(this.keypad.DtFrom === undefined ? null : moment(this.keypad.DtFrom).toDate()),
			'StartTime': new FormControl(this.getTime(this.keypad.DtFrom)),
			'EndDate': new FormControl(this.keypad.DtTo === undefined ? null : moment(this.keypad.DtTo).toDate()),
			'EndTime': new FormControl(this.getTime(this.keypad.DtTo)),
		});
		this.keypadForm.addValidators(CustomValidators.checkMatchingMtmaActionContact('Action1', 'Action2'));
		this.keypadForm.addValidators(CustomValidators.checkMatchingTemporaryRule('Rule', 'StartTime', 'EndTime', 'StartDate', 'EndDate'));

		this.mtmaKeypadModifySub = this.store.select('device', 'mtmaKeypadModify')
			.pipe(filter(mtmaKeypadModify => !!mtmaKeypadModify))
			.subscribe(() => {
				this.resetClose();
			});

		this.mtmaActionsSub = this.store.select('device', 'mtmaActions')
			.subscribe(mtmaActions => {
				this.mtmaActions = mtmaActions.filter(a => a.Keypads);
			});

		this.store.dispatch(DeviceActions.getMtmaRulesAll());
		this.listRulesSub = this.store.select('device', 'mtmaRulesAll').subscribe(listRules => {
			if (listRules === undefined) {
				this.listRules = [];
			} else {
				this.listRules = Object.assign([], listRules);
			}
			const rule = new MtmaRule();
			rule.Name = this.translate.instant('NO_GROUP');
			rule.Id = null;
			this.listRules.unshift(rule);
		});

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	ngOnDestroy(): void {
		this.mtmaKeypadModifySub?.unsubscribe();
		this.mtmaActionsSub?.unsubscribe();
		this.mtmaModuleSub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetTagForm());
		this.closeDialog();
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.keypadForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.keypadForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
				 console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save(): void {
		let startTime = ''
		if (typeof this.f.StartTime.value === 'string') {
			startTime = this.f.StartTime.value;
		} else {
			startTime = moment(this.f.StartTime.value).format('HH:mm');
		}
		let endTime = ''
		if (typeof this.f.EndTime.value === 'string') {
			endTime = this.f.EndTime.value;
		} else {
			endTime = moment(this.f.EndTime.value).format('HH:mm');
		}
		this.keypad = {
			...this.keypad,
			Description: this.f.Description.value,
			Code: this.f.Code.value,
			Actions: [this.f.Action1.value, this.f.Action2.value],
			RuleId: this.f.Rule.value === undefined || this.f.Rule.value === null ? 0 : this.f.Rule.value
		};
		if (this.keypad.RuleId !== null && this.keypad.RuleId !== undefined &&
			this.keypadForm.value.StartDate != null && this.keypadForm.value.StartTime != null &&
			this.keypadForm.value.EndDate != null && this.keypadForm.value.EndTime != null) {
			//DATE VAL
			this.keypad.DtFrom = moment(this.keypadForm.value.StartDate).format('yyyy-MM-DDT') + startTime;
			this.keypad.DtTo = moment(this.keypadForm.value.EndDate).format('yyyy-MM-DDT') + endTime;
		} else {
			this.keypad.DtFrom = undefined;
			this.keypad.DtTo = undefined;
		}
		if (this.keypad.ObjId) {
			this.store.dispatch(DeviceActions.editMtmaKeypad({ keypad: this.keypad }));
		} else {
			if (this.keypad.RuleId === 0) {
				this.keypad.RuleId = null;
			}
			this.store.dispatch(DeviceActions.addMtmaKeypad({ keypad: this.keypad }));
		}
	}

	getTime(date: string): string {
		if (date === undefined) {
			return '';
		}
		return moment(date).format('HH:mm');
	}

	getRuleInfo(tag: MtmaTag): string {
		let res = '';
		if (tag.RuleId !== undefined) {
			res = this.translate.instant('GROUP') + ': ' + tag.RuleName;
		}
		if (tag.DtFrom !== undefined && tag.DtTo !== undefined) {
			res = res + ' ' + this.translate.instant('FROM') + ' ' + this.dateTransform.transform(tag.DtFrom, 'dd/MM/yyyy HH:mm') +
				' - ' + this.translate.instant('TO') + ' ' + this.dateTransform.transform(tag.DtTo, 'dd/MM/yyyy HH:mm');
		}
		return res;
	}
	get f(): any {
		return this.keypadForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: MtmaKeypadDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: MtmaKeypadDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	dynamicInputTextDesktopTabletWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-30rem');
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

}
